import { DateTime } from 'luxon';
import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { IosLockScreenNotification } from '@/components/campaign-composer/ios-lock-screen-notification';
import {
  formatLockScreenClock,
  formatLockScreenDate,
} from '@/components/campaign-composer/korza-studio';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn, getLuxonLocale } from '@/lib/utils';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';
import { ScaledDevice } from '@/components/whats-new/editions/estreia/scaled-device';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import { studioVM } from '@/components/whats-new/editions/estreia/view-models';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { Rise } from '@/components/whats-new/slides/rise';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/** The typing starts once the form has come in. */
export const TYPING_START_MS = 1100;
/** One letter of the title, then one of the message (NEWS-31). */
export const TITLE_LETTER_MS = 80;
export const MESSAGE_LETTER_MS = 45;

// The lock screen scene is drawn for the onboarding's 300px screen, like the
// composer's own Korza Studio preview; the whole device is scaled.
const SCREEN_WIDTH = 300;

/**
 * Letters typed so far of the title, then of the message; the whole text at
 * once under reduced motion (NEWS-09). A new text or leaving the slide
 * starts over / cancels it.
 */
function useTyping(title: string, message: string) {
  const reduceMotion = useReducedMotion() ?? false;
  const total = title.length + message.length;
  const [typed, setTyped] = useState(reduceMotion ? total : 0);

  useEffect(() => {
    if (reduceMotion) {
      setTyped(total);
      return;
    }
    setTyped(0);
    let timer: ReturnType<typeof setTimeout>;
    let count = 0;
    const type = () => {
      count += 1;
      setTyped(count);
      if (count < total) {
        timer = setTimeout(
          type,
          count < title.length ? TITLE_LETTER_MS : MESSAGE_LETTER_MS
        );
      }
    };
    if (total > 0) timer = setTimeout(type, TYPING_START_MS);
    return () => clearTimeout(timer);
  }, [title.length, total, reduceMotion]);

  return {
    title: title.slice(0, Math.min(typed, title.length)),
    message: message.slice(0, Math.max(0, typed - title.length)),
    /** Still typing the title (the caret is there) or already the message. */
    caret: typed < title.length || title.length === 0 ? 'title' : 'message',
  } as const;
}

function Caret() {
  return (
    <span
      aria-hidden="true"
      className="ml-px inline-block h-[1.05em] w-[1.5px] animate-pulse bg-white align-[-2px]"
    />
  );
}

function Step({
  number,
  label,
  value,
  active = false,
}: {
  number: number;
  label: string;
  value?: string;
  active?: boolean;
}) {
  return (
    <div
      data-testid="studio-step"
      className="flex min-w-0 items-center gap-[9px] py-0.5 text-[12.5px]"
    >
      <span
        className={cn(
          'grid size-5 flex-none place-items-center rounded-full text-[11px] font-bold',
          active ? 'bg-white text-[#15191E]' : 'bg-white/10 text-white/[.86]'
        )}
      >
        {number}
      </span>
      <b className="whitespace-nowrap font-semibold">{label}</b>
      {value && (
        <span className="ml-auto min-w-0 truncate text-xs text-white/[.64]">
          {value}
        </span>
      )}
    </div>
  );
}

/**
 * 4 · Korza Studio (NEWS-31): the composer's steps with the store's latest
 * campaign being typed (or the module's sample text), and the real iOS lock
 * screen notification in the iPhone rising with the UIKit spring on the
 * first letter and updating on every letter. The preview stays iOS-faithful
 * even where its text is below 4.5:1 (user rule for device previews).
 */
export function StudioSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const facts = useEstreiaFacts();
  const reduceMotion = useReducedMotion() ?? false;
  const { store, notification } = studioVM(facts);
  const now = DateTime.fromISO(facts.period.current.to);
  const luxonLocale = getLuxonLocale(locale);

  const fullTitle =
    notification.state === 'ready' ? notification.value.title(LL) : '';
  const fullMessage =
    notification.state === 'ready' ? notification.value.message(LL) : '';
  const typed = useTyping(fullTitle, fullMessage);
  const arrived = typed.title.length > 0;

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.studio.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.studio.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>
              {LL.whatsNew.editions.estreia.studio.lead({ store: store.name })}
            </SlideLead>
          </Rise>
        </>
      }
      visual={
        <Rise
          index={1}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 max-md:grid-cols-[minmax(0,1fr)]"
        >
          {/* Below 768px only the phone: the form doesn't fit beside it. */}
          <GlassCard className="flex min-w-0 flex-col gap-3 p-4 max-md:hidden">
            <Step
              number={1}
              label={LL.whatsNew.editions.estreia.studio.steps.notification()}
              active
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-[11.5px] font-semibold tracking-[0.02em] text-white/[.64]">
                {LL.whatsNew.editions.estreia.studio.steps.titleField()}
              </span>
              <div
                data-testid="studio-title-field"
                className="min-h-[38px] rounded-lg bg-white/[.08] px-[11px] py-[9px] text-[13.5px] leading-[1.4] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
              >
                {notification.state === 'loading' ? (
                  <Skeleton className="h-4 w-2/3 bg-white/[.12]" />
                ) : (
                  <>
                    {typed.title}
                    {!reduceMotion && typed.caret === 'title' && <Caret />}
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11.5px] font-semibold tracking-[0.02em] text-white/[.64]">
                {LL.whatsNew.editions.estreia.studio.steps.messageField()}
              </span>
              <div
                data-testid="studio-message-field"
                className="min-h-[62px] rounded-lg bg-white/[.08] px-[11px] py-[9px] text-[13.5px] leading-[1.4] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
              >
                {typed.message}
                {!reduceMotion && typed.caret === 'message' && <Caret />}
              </div>
            </div>
            <Step
              number={2}
              label={LL.whatsNew.editions.estreia.studio.steps.onTap()}
              value={LL.whatsNew.editions.estreia.studio.steps.onTapValue()}
            />
            <Step
              number={3}
              label={LL.whatsNew.editions.estreia.studio.steps.when()}
              value={LL.whatsNew.editions.estreia.studio.steps.whenValue()}
            />
            <Step
              number={4}
              label={LL.whatsNew.editions.estreia.studio.steps.audience()}
              value={LL.whatsNew.editions.estreia.studio.steps.audienceValue()}
            />
            <div className="mt-0.5 grid h-9 place-items-center rounded-lg bg-white text-[13px] font-semibold text-[#15191E]">
              {LL.whatsNew.editions.estreia.studio.steps.submit()}
            </div>
          </GlassCard>

          <ScaledDevice className="mx-auto drop-shadow-[0_40px_60px_rgba(0,0,0,0.45)] [--device-scale:0.64] max-md:[--device-scale:0.6] [@media(min-width:768px)_and_(max-height:800px)]:[--device-scale:0.58]">
            <div data-testid="studio-phone">
              <IPhoneMockup
                sceneKey="lock-screen"
                screenWidth={SCREEN_WIDTH}
                chromeBaked
              >
                <div className="relative h-full w-full">
                  <div aria-hidden="true" className="h-full w-full">
                    <LockScreenScene
                      dateLabel={formatLockScreenDate(now, luxonLocale)}
                      clock={formatLockScreenClock(now, luxonLocale)}
                    />
                  </div>
                  <IosLockScreenNotification
                    title={typed.title}
                    body={typed.message}
                    titlePlaceholder={LL.notificationPreview.titlePlaceholder()}
                    bodyPlaceholder={LL.notificationPreview.bodyPlaceholder()}
                    timeLabel={LL.notificationPreview.now()}
                    iconUrl={store.iconUrl}
                    screenWidth={SCREEN_WIDTH}
                    visible={arrived}
                    reduceMotion={reduceMotion}
                  />
                </div>
              </IPhoneMockup>
            </div>
          </ScaledDevice>
        </Rise>
      }
    />
  );
}
